<template>
  <a-select
    v-model:value="innerValue"
    :options="options"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :show-search="showSearch"
    :filter-option="filterAreaOption"
    :loading="loading || internalLoading"
    :status="status"
    :not-found-content="notFoundContent"
    class="ant-province-select"
    @change="onChange"
  />
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import {
  filterAreaOption,
  loadAreaTree,
  mapAreaOptions,
  findAreaById,
  type AreaNode,
  type AreaSelectOption,
} from "./useArea";

interface Props {
  modelValue?: string | number | null;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  showSearch?: boolean;
  loading?: boolean;
  status?: "" | "error" | "warning";
  notFoundContent?: string;
  /** Nếu truyền sẵn tree thì không gọi API */
  areas?: AreaNode[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: "Chọn Tỉnh / Thành phố",
  disabled: false,
  size: "middle",
  allowClear: true,
  showSearch: true,
  loading: false,
  status: "",
  notFoundContent: "Không có dữ liệu",
  areas: undefined,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
  (
    e: "change",
    value: string | number | null,
    option: AreaSelectOption | null,
    districts: AreaNode[]
  ): void;
}>();

const internalLoading = ref(false);
const provinceList = ref<AreaNode[]>([]);

const options = computed(() => mapAreaOptions(props.areas ?? provinceList.value));

const innerValue = computed({
  get: () => props.modelValue,
  set: (val: string | number | null) => emit("update:modelValue", val),
});

onMounted(async () => {
  if (props.areas?.length) {
    provinceList.value = props.areas;
    return;
  }
  internalLoading.value = true;
  provinceList.value = await loadAreaTree();
  internalLoading.value = false;
});

const onChange = (val: string | number | null, option: any) => {
  const selected = (option as AreaSelectOption) || null;
  const node = selected?.raw || findAreaById(props.areas ?? provinceList.value, val);
  emit("update:modelValue", val);
  emit("change", val, selected, node?.children || []);
};
</script>

<style scoped>
.ant-province-select {
  width: 100%;
}
</style>

<!--
  Hướng dẫn sử dụng — Province / City Select
  Global: <h-province v-model="province" @change="onProvince" />
  Import: import { ProvinceSelect } from '@/components/ant/address'

  @change(value, option, districts)
  Tự load API AREA_CACHE + 'area-client-list'
  Docs: src/components/ant/address/README.md
-->
